import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { activeDeploymentsForPerson, operationalAssets } from './deploymentUsageModel';
import { normalizeAssetId } from './trackingModel';
import { callGearHandoff, handoffTypeLabel } from './gearHandoffApi';
import { WorkflowAlert } from './WorkflowFeedback';
import GearHandoffScanner from './GearHandoffScanner';
import { isNativeShell } from '../auth/authRedirect';

function deploymentTypeLabel(deployment) {
  return String(deployment?.deploymentType || deployment?.role || 'EMPF').trim() || 'EMPF';
}

function incidentLabel(deployment) {
  const incident = String(deployment?.incident || '').trim();
  const order = String(deployment?.incidentOrderNumber || '').trim();
  return order ? `${incident} · ${order}` : incident;
}

function formatExpiry(value) {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export default function TransferGearView({ state, actorName, onBack, onRefreshSessions }) {
  const deploymentOptions = useMemo(() => {
    const active = activeDeploymentsForPerson(state, actorName);
    const assets = operationalAssets(state);
    return active.map(deployment => ({
      deployment,
      assets: assets.filter(asset =>
        asset.currentDeploymentId === deployment.id
        && asset.status === 'deployed'
        && asset.ownership !== 'personal'
      ),
    })).filter(entry => entry.assets.length);
  }, [state, actorName]);

  const [deploymentId, setDeploymentId] = useState(() => deploymentOptions[0]?.deployment?.id || '');
  const [handoffType, setHandoffType] = useState('take_over');
  const [selected, setSelected] = useState([]);
  const [created, setCreated] = useState(null);
  const [qr, setQr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const current = deploymentOptions.find(entry => entry.deployment.id === deploymentId) || deploymentOptions[0] || null;
  const currentDeploymentId = current?.deployment?.id || '';
  const previewBrowser = typeof window !== 'undefined'
    && window.location.hostname.toLowerCase().endsWith('.vercel.app');
  const previewPathUrl = created
    ? new URL(created.handoffPath || created.handoffUrl || '', window.location.origin).toString()
    : '';
  const handoffToken = created?.handoffPath?.match(/^\/handoff\/([^/?#]+)/i)?.[1] || '';
  const nativeTestUrl = handoffToken
    ? `https://sacmetrolinemedic.app/tracking?handoff=${encodeURIComponent(decodeURIComponent(handoffToken))}`
    : '';
  const handoffUrl = created
    ? (isNativeShell()
      ? (created.handoffUrl || '')
      : previewBrowser && nativeTestUrl
        ? nativeTestUrl
        : previewPathUrl)
    : '';
  const manualFallbackUrl = previewBrowser ? previewPathUrl : handoffUrl;

  useEffect(() => {
    if (!currentDeploymentId) {
      setSelected([]);
      return;
    }
    setDeploymentId(currentDeploymentId);
    setSelected([]);
  }, [currentDeploymentId]);

  useEffect(() => {
    let cancelled = false;
    if (!handoffUrl) {
      setQr('');
      return undefined;
    }
    QRCode.toDataURL(handoffUrl, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 720,
    }).then(value => {
      if (!cancelled) setQr(value);
    }).catch(() => {
      if (!cancelled) setQr('');
    });
    return () => { cancelled = true; };
  }, [handoffUrl]);

  const toggle = id => {
    const normalized = normalizeAssetId(id);
    setSelected(currentSelected => currentSelected.includes(normalized)
      ? currentSelected.filter(item => item !== normalized)
      : [...currentSelected, normalized]);
  };

  const create = async () => {
    setError('');
    if (!current) return setError('You do not have an active deployment with transferable program gear.');
    if (!selected.length) return setError('Choose at least one physical asset to hand off.');
    setBusy(true);
    try {
      const result = await callGearHandoff('create', {
        handoffType,
        assetIds: selected,
      });
      setCreated(result);
      onRefreshSessions?.();
    } catch (createError) {
      setError(createError?.message || 'Unable to create the handoff QR.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    const sessionId = created?.session?.id;
    if (!sessionId) return;
    setBusy(true);
    setError('');
    try {
      await callGearHandoff('revoke', { sessionId });
      setCreated(null);
      setQr('');
      onRefreshSessions?.();
    } catch (revokeError) {
      setError(revokeError?.message || 'Unable to cancel the handoff.');
    } finally {
      setBusy(false);
    }
  };

  if (created?.session) {
    const session = created.session;
    return (
      <section className="tracking-card">
        <div className="tracking-section-head">
          <div>
            <span className="my-gear-eyebrow">GEAR HANDOFF READY</span>
            <h2>{handoffTypeLabel(session.handoffType)}</h2>
            <p>{session.incident}{session.incidentOrderNumber ? ` · ${session.incidentOrderNumber}` : ''}</p>
          </div>
          <button type="button" className="tracking-link-btn" onClick={onBack}>Back</button>
        </div>

        <div className="tracking-identity-note">
          <strong>Have the receiving member scan this QR from their own Line Medic account.</strong><br />
          It expires at {formatExpiry(session.expiresAt)} and can only be accepted once.
        </div>

        <div style={{ display: 'grid', justifyItems: 'center', gap: 14, margin: '20px 0' }}>
          {qr
            ? <img src={qr} alt="Gear handoff QR code" style={{ width: 'min(330px, 82vw)', borderRadius: 18, background: '#fff', padding: 12 }} />
            : <div className="tracking-empty">Preparing secure handoff QR…</div>}
          {previewBrowser && (
            <div className="tracking-identity-note" style={{ width: '100%' }}>
              <strong>Preview native-app test QR</strong><br />
              This QR uses the existing production /tracking Universal Link so the Xcode test build can open directly. Build the iPhone with <code>npm run ios:sync:preview</code> so Gear Handoff API calls stay on this 1.3 Preview.
            </div>
          )}
          <div style={{ textAlign: 'center', maxWidth: 520 }}>
            <strong>{session.acceptedAssetIds?.length || session.assetIds?.length || 0} physical assets</strong>
            <p style={{ marginTop: 5 }}>
              {session.handoffType === 'return_on_behalf'
                ? 'The receiver will only get temporary return custody. Your deployment history and operational assignment stay intact until the gear is actually returned.'
                : 'When accepted, the selected gear moves to the receiving member for continued operational use.'}
            </p>
          </div>
          <div className="my-gear-grid" style={{ width: '100%' }}>
            {(session.assets || []).map(asset => (
              <div className="my-gear-card" key={asset.id} style={{ cursor: 'default' }}>
                <strong>{asset.name || asset.id}</strong>
                <small>{asset.id}</small>
              </div>
            ))}
          </div>
          <details className="my-gear-manual-fallback" style={{ width: '100%' }}>
            <summary>Can't scan the QR?</summary>
            <div className="tracking-identity-note" style={{ marginTop: 10, overflowWrap: 'anywhere' }}>
              Open this secure one-time link on the receiving member's device:<br />
              <strong>{manualFallbackUrl}</strong>
            </div>
          </details>
        </div>

        <button type="button" className="tracking-btn" disabled={busy} onClick={revoke}>
          {busy ? 'Cancelling…' : 'Cancel Handoff'}
        </button>
        <WorkflowAlert message={error} title="Handoff couldn't continue" onClose={() => setError('')} />
      </section>
    );
  }

  return (
    <section className="tracking-card">
      <div className="tracking-section-head">
        <div>
          <h2>Transfer / Receive Gear</h2>
          <p>Send accountable gear to another member or scan a handoff QR when you are receiving it.</p>
        </div>
        <button type="button" className="tracking-link-btn" onClick={onBack}>Back</button>
      </div>

      <div className="tracking-gear-block">
        <h3>Receiving gear?</h3>
        <p>Keep the handoff inside Line Medic. Open the scanner here and scan the QR on the outgoing member’s screen.</p>
        <GearHandoffScanner />
      </div>

      {!deploymentOptions.length && (
        <div className="tracking-empty">You do not have transferable program gear assigned right now.</div>
      )}

      {deploymentOptions.length > 0 && (
        <div className="tracking-form">
          {deploymentOptions.length > 1 && (
            <label>Deployment
              <select value={current?.deployment?.id || ''} onChange={event => {
                setDeploymentId(event.target.value);
                setSelected([]);
              }}>
                {deploymentOptions.map(entry => (
                  <option key={entry.deployment.id} value={entry.deployment.id}>
                    {incidentLabel(entry.deployment)} · {deploymentTypeLabel(entry.deployment)}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="tracking-identity-note">
            <strong>{incidentLabel(current?.deployment)}</strong><br />
            {deploymentTypeLabel(current?.deployment)} · {current?.assets?.length || 0} transferable physical assets
          </div>

          <div className="tracking-gear-block">
            <h3>What is happening to the gear?</h3>
            <div className="my-gear-grid">
              <button
                type="button"
                className={`my-gear-card${handoffType === 'take_over' ? ' selected' : ''}`}
                onClick={() => setHandoffType('take_over')}
              >
                <strong>Take Over Gear</strong>
                <small>Relief / crew swap</small>
                <span>Receiver becomes responsible for the selected gear.</span>
              </button>
              <button
                type="button"
                className={`my-gear-card${handoffType === 'return_on_behalf' ? ' selected' : ''}`}
                onClick={() => setHandoffType('return_on_behalf')}
              >
                <strong>Return Gear for Me</strong>
                <small>Transport back to cache</small>
                <span>Receiver carries it back; your assignment history stays intact.</span>
              </button>
            </div>
          </div>

          <div className="tracking-gear-block">
            <div className="tracking-section-head">
              <div><h3>Select physical gear</h3><p>Only program-owned accountable assets can be handed off.</p></div>
              <button type="button" className="tracking-link-btn" onClick={() => setSelected(current?.assets?.map(asset => normalizeAssetId(asset.id)) || [])}>Select all</button>
            </div>
            <div className="tracking-asset-chips">
              {(current?.assets || []).map(asset => {
                const id = normalizeAssetId(asset.id);
                const chosen = selected.includes(id);
                return (
                  <button type="button" key={id} className={`tracking-asset-chip${chosen ? ' selected' : ''}`} onClick={() => toggle(id)}>
                    <strong>{asset.name}</strong>
                    <small>{id}</small>
                  </button>
                );
              })}
            </div>
          </div>

          <button type="button" className="tracking-btn tracking-btn-primary" disabled={busy || !selected.length} onClick={create}>
            {busy ? 'Creating secure handoff…' : `Create ${handoffTypeLabel(handoffType)} QR`}
          </button>
        </div>
      )}

      <WorkflowAlert message={error} title="Handoff couldn't continue" onClose={() => setError('')} />
    </section>
  );
}
